import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/_services/global.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { UserService } from 'src/app/_services/user.service';

/** Shown by AuthGuard instead of navigating a still-deploying account (accountStatusString ===
 *  'DeploymentInProgress') straight back into the signup wizard, which used to discard the
 *  backend's own "wait, you'll get an email" message and dump the user onto signup step 1 with no
 *  explanation. Polls getUserByUserName() (token-based — no stored password needed, unlike
 *  subscription-payment-success.component.ts's checkDeploymentStatus()) every 5s and continues
 *  automatically to whatever route the guard originally blocked once deployment finishes. */
@Component({
    selector: 'app-deployment-pending',
    templateUrl: './deployment-pending.component.html',
    styleUrls: ['./deployment-pending.component.scss'],
})
export class DeploymentPendingComponent implements OnInit, OnDestroy {
    _globals = GlobalService;

    readonly waitingMessage = "We're finishing setting up your account. This usually takes less than a minute — you'll be redirected automatically once it's ready.";
    timedOut = false;

    private pollHandle: any = null;
    private attempts = 0;
    private readonly maxAttempts = 60; // 60 * 5s = 5 minutes

    constructor(
        private _userService: UserService,
        private _router: Router,
        public _abd: AppBaseDataService,
    ) { }

    ngOnInit(): void {
        this.poll();
    }

    ngOnDestroy(): void {
        if (this.pollHandle) clearTimeout(this.pollHandle);
    }

    retryNow(): void {
        this.timedOut = false;
        this.attempts = 0;
        this.poll();
    }

    private poll(): void {
        const username = this._globals.userInfo?.username || this._userService.currentUserObj?.userName;
        if (!username) {
            this._router.navigate(['pages/signin']);
            return;
        }
        this._userService.getUserByUserName(username).subscribe({
            next: (res: any) => {
                if (res?.success && res?.data) {
                    this._globals.userInfo = res.data;
                    const status = res.data?.businessSetup?.[0]?.accountStatusString || '';
                    if (status !== 'DeploymentInProgress') {
                        this.continueToDestination();
                        return;
                    }
                }
                this.scheduleNextPoll();
            },
            error: () => this.scheduleNextPoll(),
        });
    }

    private scheduleNextPoll(): void {
        this.attempts++;
        if (this.attempts >= this.maxAttempts) {
            this.timedOut = true;
            return;
        }
        this.pollHandle = setTimeout(() => this.poll(), 5000);
    }

    private continueToDestination(): void {
        const returnUrl = sessionStorage.getItem('deployment-pending-return-url') || this._abd.getDashboardRoute();
        sessionStorage.removeItem('deployment-pending-return-url');
        this._router.navigateByUrl(returnUrl);
    }
}
